export const WORKFLOW_TEMPLATE_CATEGORIES = [
  { id: "popular", label: "Popular" },
  { id: "finance", label: "Finance" },
  { id: "hr", label: "HR" },
  { id: "support", label: "Support" },
  { id: "sales", label: "Sales" },
  { id: "operations", label: "Operations" },
  { id: "legal", label: "Legal" },
  { id: "it", label: "IT" },
];

export const WORKFLOW_TEMPLATE_CATEGORY_IDS = WORKFLOW_TEMPLATE_CATEGORIES.map(
  (category) => category.id
);

export const WORKFLOW_TEMPLATE_PROMPTS = [
  {
    id: "invoice-approval",
    title: "Invoice approval",
    shortDescription:
      "Extract invoice details, route approvals by amount, and update finance records.",
    category: "finance",
    tags: ["invoice", "approval", "finance", "accounts payable"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build an invoice approval workflow. The workflow should support a trigger form for uploaded invoices and an email trigger for invoices sent by vendors. Required data includes vendor name, invoice number, amount, currency, due date, requester, line items, and the original invoice file.

Core steps:
1. Use an AI agent to extract invoice fields and flag missing or suspicious values.
2. Store each invoice in a database table with status, requester, amount, approver, due date, and extraction confidence.
3. Use a gateway to route invoices by amount: low value can go to finance review, medium value needs manager approval, and high value needs finance plus executive approval.
4. Create dynamic review tasks for the required approvers.
5. Merge or converge approval threads before final processing.
6. Send email updates to the requester and finance team.
7. Update the database table with approved, rejected, or needs-info status.

Human review points: extracted data validation, approval decision, rejection reason, and exception handling.

Outputs: an updated invoice record, approval audit trail, requester notification, finance notification, and a summary document for high-value invoices.

Assumptions: if exact amount thresholds are not provided, propose sensible defaults and ask me to confirm them before building.`,
  },
  {
    id: "employee-onboarding",
    title: "Employee onboarding",
    shortDescription:
      "Coordinate new-hire setup tasks, welcome communications, and follow-up reporting.",
    category: "hr",
    tags: ["onboarding", "hr", "new hire", "tasks"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build an employee onboarding workflow. The workflow should start from a trigger form that captures new-hire details: full name, role, department, manager, start date, location, equipment needs, system access needs, and personal email.

Core steps:
1. Create a new employee onboarding row in a database table.
2. Use an AI agent to summarize onboarding requirements and identify missing information.
3. Start parallel dynamic review tasks for IT, facilities, and the hiring manager.
4. Send a welcome email to the new hire with first-day instructions.
5. Use a gateway to route remote, hybrid, and office-based hires through the right setup steps.
6. Merge setup threads when IT, facilities, and manager tasks are complete.
7. Add a 30-day check-in delay and send a follow-up task to the manager.
8. Use the document editor to create a final onboarding summary.

Human review points: manager confirmation, IT access approval, facilities readiness, and 30-day check-in notes.

Outputs: onboarding database status, task completion audit trail, welcome email, manager check-in reminder, and final summary document.

Assumptions: if the company has no fixed onboarding SLA, propose default deadlines for IT, facilities, and manager tasks before building.`,
  },
  {
    id: "support-ticket-triage",
    title: "Support ticket triage",
    shortDescription:
      "Classify customer issues, escalate urgent tickets, and summarize support trends.",
    category: "support",
    tags: ["support", "ticket", "triage", "customer"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build a support ticket triage workflow. The workflow should start from a trigger form or support email trigger that captures customer name, customer tier, account identifier, issue description, attachments, and requested urgency.

Core steps:
1. Use an AI agent to classify category, urgency, sentiment, and likely next action.
2. Create or update a ticket record in a database table.
3. Use a gateway to route VIP customers, critical incidents, and negative sentiment tickets to escalation.
4. Create a dynamic review task for the assigned support owner.
5. Send status emails to the customer when a ticket is created, escalated, and resolved.
6. Use Slack for internal escalation notifications when urgency is critical.
7. On a weekly schedule trigger, summarize ticket volume, categories, and trends.

Human review points: assigned owner, escalation decision, customer response quality, and resolution confirmation.

Outputs: ticket database record, customer emails, Slack escalation messages, and a weekly trend summary document.

Assumptions: if customer tiers or severity levels are not defined, propose a simple routing model before building.`,
  },
  {
    id: "contract-review",
    title: "Contract review",
    shortDescription:
      "Extract contract risks, route legal approvals, and track renewal obligations.",
    category: "legal",
    tags: ["contract", "legal", "risk", "approval"],
    featured: true,
    integrations: ["Email", "Forms", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build a contract review workflow. The workflow should start from an email trigger or trigger form where a requester submits a contract file, counterparty, contract type, estimated value, requested deadline, and business owner.

Core steps:
1. Use an AI agent to extract parties, contract value, effective date, renewal date, termination notice period, governing law, risky clauses, and missing sections.
2. Store the contract review record in a database table.
3. Use a gateway to route high-risk or high-value contracts to legal approval and standard contracts to business-owner review.
4. Create dynamic review tasks for legal, finance, and business owner when required.
5. Merge approval threads before final decision.
6. Send final email with approval, rejection, or required edits.
7. Update contract status and renewal reminder fields in the database.

Human review points: risk assessment, clause approval, final decision, and required edits.

Outputs: contract database record, approval audit trail, final email, and a document editor summary of key terms and risks.

Assumptions: if risk thresholds are not defined, propose defaults for value, renewal length, and risky clause categories before building.`,
  },
  {
    id: "sales-lead-qualification",
    title: "Sales lead qualification",
    shortDescription:
      "Score inbound leads, create sales tasks, and send tailored follow-up.",
    category: "sales",
    tags: ["sales", "lead", "qualification", "crm"],
    featured: true,
    integrations: ["Forms", "Email", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build a sales lead qualification workflow. The workflow should start from a website trigger form that captures name, company, email, phone, role, company size, region, use case, budget range, timeline, and freeform notes.

Core steps:
1. Use an AI agent to score fit, urgency, potential value, and next-best action.
2. Create a CRM-style lead record in a database table.
3. Use a gateway to route qualified leads to a sales dynamic review task and lower-score leads to nurture.
4. Send a personalized follow-up email based on the lead's use case and urgency.
5. Notify the sales team in Slack for high-priority leads.
6. Add a scheduled reminder or delay for nurture follow-up.
7. Update lead status after owner review.

Human review points: score override, sales owner assignment, qualification decision, and follow-up approval.

Outputs: lead database record, personalized email, sales task, Slack alert for high-priority leads, and nurture reminder for lower-score leads.

Assumptions: if qualification criteria are not defined, propose a simple fit and urgency scoring rubric before building.`,
  },
  {
    id: "vendor-onboarding",
    title: "Vendor onboarding",
    shortDescription:
      "Collect vendor details, assess risk, approve procurement, and send onboarding emails.",
    category: "operations",
    tags: ["vendor", "procurement", "onboarding", "risk"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "Document editor"],
    prompt: `Please first produce a concise plan before building anything.

Build a vendor onboarding workflow. The workflow should start from a trigger form that captures vendor company name, contact details, tax information, services provided, certifications, insurance documents, regions served, data access needs, and requester.

Core steps:
1. Use an AI agent to validate completeness and identify risk indicators.
2. Store the vendor record in a database table.
3. Use a gateway to route high-risk vendors for extra legal or security review.
4. Create a dynamic review task for procurement approval.
5. Create additional dynamic review tasks for legal, security, or finance when required.
6. Merge approvals before final onboarding.
7. Send a welcome email after approval and update vendor status.
8. Use the document editor to create a vendor onboarding summary.

Human review points: procurement approval, risk override, legal/security review, and final onboarding decision.

Outputs: vendor database record, approval audit trail, welcome email, and vendor summary document.

Assumptions: if high-risk criteria are not defined, propose defaults for data access, region, contract value, and missing certifications before building.`,
  },
  {
    id: "compliance-training-tracker",
    title: "Compliance training tracker",
    shortDescription:
      "Check training completion, send reminders, capture proof, and report compliance.",
    category: "operations",
    tags: ["compliance", "training", "reminders", "reporting"],
    featured: false,
    integrations: ["Schedule", "Email", "Forms", "Database"],
    prompt: `Please first produce a concise plan before building anything.

Build a compliance training tracker workflow. The workflow should start from a schedule trigger that checks a training database table for users who are incomplete, overdue, or nearing a deadline.

Core steps:
1. Loop through the list of users with incomplete training.
2. Use a gateway to route users by due soon, overdue, and complete status.
3. Send reminder emails to incomplete users and escalation emails for overdue users.
4. Use a trigger form to capture proof of completion or exemption requests.
5. Update each user's compliance status in the database table.
6. Add a delay for follow-up reminders when no proof is received.
7. Use the document editor to create a compliance report for managers.

Human review points: exemption approval, overdue escalation, and final compliance report review.

Outputs: updated compliance database rows, reminder emails, escalation emails, proof records, and compliance summary document.

Assumptions: if training deadlines are not defined, propose default reminder timing and escalation rules before building.`,
  },
  {
    id: "project-intake-and-approval",
    title: "Project intake and approval",
    shortDescription:
      "Summarize project requests, route approvals, and notify teams of decisions.",
    category: "operations",
    tags: ["project", "intake", "approval", "budget"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build a project intake and approval workflow. The workflow should start from a trigger form that captures requester, project name, business goal, budget, timeline, required resources, expected impact, risks, and strategic priority.

Core steps:
1. Use an AI agent to summarize scope, business value, resource needs, and risks.
2. Store the project request in a database table.
3. Use a gateway to route requests by budget, risk, and strategic priority.
4. Create dynamic review tasks for the required approvers.
5. Merge approvals before the final decision.
6. Send an email with the approval, rejection, or request-for-more-information decision.
7. Send Slack kickoff details for approved projects.
8. Update request status in the database.

Human review points: budget approval, strategic priority review, risk assessment, and final decision.

Outputs: project request database record, approval audit trail, decision email, Slack kickoff message, and summary document.

Assumptions: if approval thresholds are not defined, propose default budget and priority rules before building.`,
  },
  {
    id: "customer-feedback-analysis",
    title: "Customer feedback analysis",
    shortDescription:
      "Analyze feedback sentiment, escalate urgent issues, and summarize customer themes.",
    category: "support",
    tags: ["feedback", "sentiment", "customer success", "analysis"],
    featured: false,
    integrations: ["Email", "Forms", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build a customer feedback analysis workflow. The workflow should start from an email trigger, API-triggered submission, or trigger form that captures customer identity, feedback text, source, product area, account owner, and attachments.

Core steps:
1. Use an AI agent to extract sentiment, themes, urgency, feature requests, and churn risk.
2. Store feedback in a database table for trend analysis.
3. Use a gateway to escalate negative, urgent, or churn-risk feedback.
4. Create a dynamic review task for customer success follow-up.
5. Send acknowledgement emails when appropriate.
6. Notify Slack for urgent negative feedback.
7. On a monthly schedule trigger, summarize top themes and trend changes.

Human review points: escalation decision, customer success response, theme validation, and monthly insights approval.

Outputs: feedback database record, follow-up task, customer email, Slack escalation, and monthly insights document.

Assumptions: if sentiment escalation criteria are not defined, propose default thresholds before building.`,
  },
  {
    id: "marketing-campaign-approval",
    title: "Marketing campaign approval",
    shortDescription:
      "Review campaign briefs, coordinate approvals, and update launch readiness.",
    category: "sales",
    tags: ["marketing", "campaign", "approval", "brand"],
    featured: false,
    integrations: ["Forms", "Email", "Database", "Slack"],
    prompt: `Please first produce a concise plan before building anything.

Build a marketing campaign approval workflow. The workflow should start from a trigger form that captures campaign name, owner, launch date, channel, budget, brief, target audience, assets, claims, and required reviewers.

Core steps:
1. Use an AI agent to check the brief and assets for brand, policy, and compliance concerns.
2. Store the campaign request in a database table.
3. Use a gateway to route by channel, budget, claims, and risk level.
4. Create parallel dynamic review tasks for legal, brand, and budget owners.
5. Merge or converge approval threads before final decision.
6. Send final launch approval or revision-request email.
7. Notify Slack when a campaign is approved for launch.
8. Update campaign status in the database.

Human review points: legal approval, brand approval, budget approval, and final launch decision.

Outputs: campaign database record, approval audit trail, decision email, Slack launch notification, and campaign summary document.

Assumptions: if review requirements differ by channel, propose a default routing matrix before building.`,
  },
];

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export function getWorkflowTemplateById(id) {
  const normalizedId = normalize(id);
  return (
    WORKFLOW_TEMPLATE_PROMPTS.find(
      (template) => normalize(template.id) === normalizedId
    ) || null
  );
}

export function getFeaturedWorkflowTemplatePrompts() {
  return WORKFLOW_TEMPLATE_PROMPTS.filter((template) => template.featured).map(
    ({ title, prompt }) => ({ title, prompt })
  );
}

export function searchWorkflowTemplates(query = "", category = "all") {
  const normalizedQuery = normalize(query);
  const normalizedCategory = normalize(category);

  return WORKFLOW_TEMPLATE_PROMPTS.filter((template) => {
    const categoryMatches =
      !normalizedCategory ||
      normalizedCategory === "all" ||
      (normalizedCategory === "popular"
        ? template.featured
        : normalize(template.category) === normalizedCategory);

    if (!categoryMatches) return false;
    if (!normalizedQuery) return true;

    const searchableParts = [
      template.title,
      template.shortDescription,
      template.category,
      ...(template.tags || []),
      ...(template.integrations || []),
    ];

    return searchableParts.some((part) =>
      normalize(part).includes(normalizedQuery)
    );
  });
}
